/**
 * =============================================================================
 * Hook: useAffiliateMyCampaigns
 * Versão: 3.0.0 (Fase 5 — Galeria com KPIs ampliados)
 *
 * Descrição:
 *   Lista as campanhas ativas dos programas em que o afiliado é membro com:
 *     - Dados da campanha (nome, descrição, programa, capa de mídia)
 *     - Link pessoal (se já existir) com cliques e indicações
 *     - Conversões e valor total ganho (sum em affiliate_commissions)
 *     - Flag has_terms + terms_template_id (para o botão "Regras")
 *
 *   Toda a agregação é feita no servidor numa única RPC
 *   (`list_my_campaigns_with_metrics`) — substitui o N+1 anterior de
 *   `get_or_create_campaign_link` em loop. A criação do link agora é
 *   *lazy*: só é executada quando o afiliado interage (copy/QR/share)
 *   via `useEnsureCampaignLink`.
 * =============================================================================
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAffiliateAuth } from '@/hooks/useAffiliateAuth';
import { useAffiliateMyMemberships } from '@/hooks/useAffiliateMyMemberships';
import { createLogger } from '@/lib/logger';

const log = createLogger('useAffiliateMyCampaigns');

/** Item retornado pela RPC `list_my_campaigns_with_metrics`. */
export interface MyCampaignLink {
  // Programa
  program_id: string;
  program_name: string;
  // Campanha
  campaign_id: string;
  campaign_name: string;
  campaign_slug: string;
  campaign_description: string | null;
  destination_url: string | null;
  // Capa
  cover_media_url: string | null;
  cover_media_type: 'image' | 'video' | null;
  // Regras
  has_terms: boolean;
  terms_template_id: string | null;
  terms_template_name: string | null;
  // Configuração do formulário de nova indicação (v3.1.0)
  /** Exibe o campo E-mail no modal "Nova Indicação". */
  referral_form_show_email: boolean;
  /** Exibe o campo Observação no modal "Nova Indicação". */
  referral_form_show_message: boolean;
  // Prazos da campanha (ISO timestamptz). Quando definidos, o card
  // exibe um contador regressivo (escassez) na capa.
  starts_at: string | null;
  ends_at: string | null;
  // Link pessoal (pode ser null se ainda não foi criado)
  link_id: string | null;
  /** Slug do link pessoal (alias mantido como `slug` para compatibilidade). */
  slug: string | null;
  short_code: string | null;
  clicks_count: number;
  referrals_count: number;
  is_active: boolean;
  // Métricas de comissão
  conversions_count: number;
  earned_amount: number;
  pending_amount: number;
  currency: string;
}

const QK_BASE = 'affiliate-my-campaigns';

export function useAffiliateMyCampaigns() {
  const { affiliate, ready } = useAffiliateAuth();
  const { isLoading: membershipsLoading } = useAffiliateMyMemberships();

  return useQuery({
    queryKey: [QK_BASE, affiliate?.id],
    enabled: ready && !!affiliate?.id && !membershipsLoading,
    queryFn: async (): Promise<MyCampaignLink[]> => {
      const { data, error } = await supabase.rpc(
        'list_my_campaigns_with_metrics' as never,
      );
      if (error) {
        log.error('Erro ao listar campanhas com métricas', error);
        throw error;
      }
      // Tipo do payload bruto da RPC (campos podem chegar como link_slug)
      type RawRow = Partial<MyCampaignLink> & {
        link_slug?: string | null;
      };
      const rows = ((data as unknown) as RawRow[]) ?? [];
      return rows.map((r) => ({
        program_id: r.program_id,
        program_name: r.program_name ?? 'Programa',
        campaign_id: r.campaign_id,
        campaign_name: r.campaign_name,
        campaign_slug: r.campaign_slug,
        campaign_description: r.campaign_description ?? null,
        destination_url: r.destination_url ?? null,
        cover_media_url: r.cover_media_url ?? null,
        cover_media_type: (r.cover_media_type ?? null) as
          | 'image'
          | 'video'
          | null,
        has_terms: !!r.has_terms,
        terms_template_id: r.terms_template_id ?? null,
        terms_template_name: r.terms_template_name ?? null,
        // Default true garante compatibilidade com campanhas legadas.
        referral_form_show_email: r.referral_form_show_email ?? true,
        referral_form_show_message: r.referral_form_show_message ?? true,
        starts_at: (r as { starts_at?: string | null }).starts_at ?? null,
        ends_at: (r as { ends_at?: string | null }).ends_at ?? null,
        link_id: r.link_id ?? null,
        slug: r.link_slug ?? null,
        short_code: r.short_code ?? null,
        clicks_count: Number(r.clicks_count ?? 0),
        referrals_count: Number(r.referrals_count ?? 0),
        is_active: r.is_active ?? true,
        conversions_count: Number(r.conversions_count ?? 0),
        earned_amount: Number(r.earned_amount ?? 0),
        pending_amount: Number(r.pending_amount ?? 0),
        currency: r.currency ?? 'BRL',
      }));
    },
    staleTime: 30_000,
  });
}

// -----------------------------------------------------------------------------
// Hook auxiliar: garante a existência do link pessoal *on demand*
// -----------------------------------------------------------------------------

/**
 * Cria/retorna o link pessoal do afiliado para uma campanha somente
 * quando solicitado (ex.: clique em copy/QR/share). Após resolver, o cache
 * de campanhas é atualizado para refletir o `link_id` recém-criado.
 */
export function useEnsureCampaignLink() {
  const qc = useQueryClient();
  const { affiliate } = useAffiliateAuth();

  return useMutation({
    mutationFn: async (params: {
      campaignId: string;
    }): Promise<{
      id: string;
      slug: string;
      short_code: string | null;
      is_active: boolean;
    }> => {
      const { data, error } = await supabase.rpc('get_or_create_campaign_link', {
        p_campaign_id: params.campaignId,
        p_tenant_hint: affiliate?.tenant_id ?? null,
      });
      if (error) {
        log.error('Falha ao garantir link da campanha', { error });
        throw error;
      }
      const row = (Array.isArray(data) ? data[0] : data) as {
        id: string;
        slug: string;
        short_code?: string | null;
        is_active?: boolean | null;
      };
      return {
        id: row.id,
        slug: row.slug,
        short_code: row.short_code ?? null,
        is_active: row.is_active ?? true,
      };
    },
    onSuccess: (link, vars) => {
      // Atualização otimista do cache local sem refetch.
      qc.setQueryData<MyCampaignLink[] | undefined>(
        [QK_BASE, affiliate?.id],
        (prev) => {
          if (!prev) return prev;
          return prev.map((c) =>
            c.campaign_id === vars.campaignId
              ? {
                  ...c,
                  link_id: link.id,
                  slug: link.slug,
                  short_code: link.short_code,
                  is_active: link.is_active,
                }
              : c,
          );
        },
      );
    },
  });
}
